import Enumerable from "linq";
import {
  Document,
  EditPhaseKinds,
  ExternalScriptParameter,
  DocumentEditBehavior,
  DocumentParameterContainer,
} from "../../../../domain/document";
import store from "../../../../store";
import { TimeMeasure } from "../../../../utilities";
import {
  FontSizeInitializer,
  HisInformationApplicator,
  InitialSettingCreateDateApplicator,
  InitialSettingFirstApplicator,
  PatientProfileApplicator,
  PreEditDocument,
  MischievousHisInformationApplicator,
  MoneyFormatInitializer,
  EditRuleSetApplicator,
} from ".";
import { LoggerFactory } from "../../../../utilities/logger";
import { User } from "../../../../domain/user";
import { ItemHeightInitializer } from "./ItemHeightInitializer";
import { IConfiguration } from "../../../../domain/configuration";
import { DepartmentRelatedHisInformationMerger } from "../../../../domain/his";
import { UserAuthority } from "../../../../domain/authentication";

export class DocumentInitializer {
  constructor(source: Document) {
    this._source = new PreEditDocument(source);
  }
  private readonly _source: PreEditDocument;

  public get isExternalScriptCompleted(): boolean {
    // ここのexternalScriptRunnerは現状では初期化終了時に破棄されるので、明示的なフラグのクリアはしていない
    return !this._source.externalScriptRunner.hasError;
  }

  async initializeNewDocument(orderNo: string, systemKey: string, linkKey: string, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, parameterContainer: DocumentParameterContainer, behavior: DocumentEditBehavior, managers: string[] = null, ...phases: EditPhaseKinds[]): Promise<void> {
    await TimeMeasure.logAsync(
      "新規文書初期化",
      async () => {
        // 新規文書では診療科は設定されていないので、ログイン診療科を設定している（設定する場所はここではないほうがよさそうな気がする）
        if (!this._source.source.attributes.department) {
          this._source.source.attributes.department = store.getters.department;
        }
        // 文書新規登録時に紐付けるオーダ情報（文書編集時は不要）
        this._source.source.attributes.systemKey = systemKey;
        this._source.source.attributes.linkKey = linkKey;
        // 新規作成時はオーダ番号は格納されていないので、ここで格納する
        if (!this._source.source.attributes.orderNo) {
          this._source.source.attributes.orderNo = orderNo;
        }
        // 文書新規登録時に紐付ける紹介情報ID情報（文書編集時は不要）
        this._source.source.attributes.institutionNo = institutionNo;
        this._source.source.attributes.regionalInstitutionNo = regionalInstitutionNo;
        // HisInfoGetterParameterは外部処理でも使う仕様を追加しているが、
        // 基本的には新規記載時のみしか外部処理をキックしない想定
        if (!this._source.source.attributes.hisInfoGetterParameter) {
          this._source.source.attributes.hisInfoGetterParameter = hisInfoGetterParameter;
        }
        this._source.source.attributes.parameterContainer = parameterContainer;
        this._source.source.attributes.behavior = behavior;
        this.updateExtraUserInformation();
        this._source.source.hisInformation.managerIds = this.getManagerIds(managers);
        await this._initializeNewDocument(
          this.createExternalScriptParameter(this._source.source),
          phases);
      },
      "(ID:" + (this._source.source.documentId ? this._source.source.documentId : "") +
      ", FID:" + this._source.source.attributes.format.id +
      ", orderNo:" + (orderNo ? orderNo : "") +
      ", systemKey:" + (systemKey ? systemKey : "") +
      ", linkKey:" + (linkKey ? linkKey : "") +
      ")",
    );
  }

  private updateExtraUserInformation(): void {
    if (!this._source.source.attributes.behavior) {
      this._logger.error("behaviorが格納されていません。実装を確認してください。");
      return;
    }
    const config = this.configuration.behavior.document.editor.documentAttributes;
    const isNewDocument = !this._source.source.documentId;
    if ((isNewDocument || config.orderUser.shouldUpdateOnEditStart) && !!store.getters.orderUser) {
      this._source.source.attributes.orderUser = store.getters.orderUser;
    }
    if ((isNewDocument || config.attendingUser.shouldUpdateOnEditStart) && !!store.getters.attendingUser) {
      this._source.source.attributes.attendingUser = store.getters.attendingUser;
    }
  }

  async initializeNewExtraDocument(destination: Document, managers: string[] = null, ...phases: EditPhaseKinds[]): Promise<void> {
    await TimeMeasure.logAsync(
      "新規続紙文書初期化",
      async () => {
        this._source.source.attributes.parameterContainer = destination.attributes.parameterContainer;
        this._source.source.attributes.behavior = destination.attributes.behavior;
        this.updateExtraUserInformation();
        this._source.source.hisInformation.items = destination.hisInformation.items.concat(); // TODO: 他の項目もコピーしたほうがよい？
        this._source.source.hisInformation.managerIds = this.getManagerIds(managers);
        await this._initializeNewDocument(
          this.createExternalScriptParameter(destination),
          phases);
      },
      "(ID:" + (this._source.source.documentId ? this._source.source.documentId : "") + ",FID:" + this._source.source.attributes.format.id + ")",
    );
  }

  private async _initializeNewDocument(parameter: ExternalScriptParameter, phases: EditPhaseKinds[]): Promise<void> {
    const editRuleSetApplicator = new EditRuleSetApplicator(this._source, this.getUser());
    await editRuleSetApplicator.apply();
    const departmentRelatedHisInformationMerger = new DepartmentRelatedHisInformationMerger();
    departmentRelatedHisInformationMerger.mergeDepartmentRelatedHisInformation(this._source.source.hisInformation, this._source.source.attributes.department);
    this._logger.info("診療科HIS項目マージ完了");
    const initialSettingCreateDateApplicator = new InitialSettingCreateDateApplicator(this._source);
    initialSettingCreateDateApplicator.apply(this._source.source.hisInformation);
    this._logger.info("初期設定作成日属性初期化完了");
    const hisApplicator = new HisInformationApplicator(this._source);
    const progresses = Enumerable.from([ EditPhaseKinds.EditStartFirst ]).concat(phases).distinct().toArray();
    this._source.source.attributes.phases = progresses;
    hisApplicator.applyByProgresses(
      this._source.source.hisInformation,
      progresses,
      parameter.user,
      parameter.department,
      this.getOrderUser(),
      this.getAttendingUser(),
    );
    this._source.source.isNewHisInformationApplied = true;
    this._logger.info("HIS情報初期化完了");
    const mischievousApplicator = new MischievousHisInformationApplicator(this._source, this.getUser(), this.isOrderableAuthority());
    mischievousApplicator.apply();
    this._logger.info("特殊HIS情報初期化完了");
    if (this.configuration.behavior.document.editor.patientProfile.isEnable) {
      const patientProfileApplicator = new PatientProfileApplicator(this._source);
      patientProfileApplicator.apply(this._source.source.patientProfile);
      this._logger.info("患者プロフィール適用完了");
    }
    const initialSettingFirstApplicator = new InitialSettingFirstApplicator(this._source, this.configuration);
    await initialSettingFirstApplicator.apply();
    this._logger.info("初期設定先頭属性初期化完了");
    const moneyFormatInitializer = new MoneyFormatInitializer(this._source);
    moneyFormatInitializer.initialize();
    this._logger.info("金額表示フォーマット初期化完了");
    const fontSizeInitializer = new FontSizeInitializer(this._source);
    fontSizeInitializer.initialize();
    this._logger.info("フォントサイズ初期化完了");
    const shouldExecuteAsStackedRequest = this.configuration.behavior.document.editor.externalScript.shouldExecuteAsStackedRequest;
    await this._source.externalScriptRunner.executeScriptInDocumentAsync(EditPhaseKinds.ManualUpdate, parameter, shouldExecuteAsStackedRequest);
    await this._source.externalScriptRunner.executeScriptInDocumentAsync(EditPhaseKinds.EditStartFirst, parameter, shouldExecuteAsStackedRequest);
    await this._source.externalScriptRunner.executeStackedScriptsAsync(parameter);
    this._logger.info("外部スクリプト実行完了");
    const itemHeightInitializer = new ItemHeightInitializer(this._source);
    itemHeightInitializer.initialize();
    this._logger.info("オブジェクト高さ初期化完了");
  }

  private createExternalScriptParameter(source: Document): ExternalScriptParameter {
    return new ExternalScriptParameter(
      source.attributes.patient,
      store.getters.user,
      source.attributes.department,
      source.attributes.orderNo,
      source.attributes.format.originalId,
      source.attributes.hisInfoGetterParameter);
  }

  async initializeExistingDocument(orderNo: string, parameterContainer: DocumentParameterContainer, behavior: DocumentEditBehavior, managers: string[] = null, ...phases: EditPhaseKinds[]): Promise<void> {
    await TimeMeasure.logAsync(
      "既存文書初期化",
      async () => {
        this._source.source.attributes.parameterContainer = parameterContainer;
        this._source.source.attributes.behavior = behavior;
        this.updateExtraUserInformation();
        this._source.source.hisInformation.managerIds = this.getManagerIds(managers);
        await this._initializeExistingDocument(
          this.createExternalScriptParameter(this._source.source),
          phases);
      },
      "(ID:" + (this._source.source.documentId ? this._source.source.documentId : "") + ",FID:" + this._source.source.attributes.format.id + ")",
    );
  }

  private async _initializeExistingDocument(parameter: ExternalScriptParameter, phases: EditPhaseKinds[]): Promise<void> {
    const editRuleSetApplicator = new EditRuleSetApplicator(this._source, this.getUser());
    await editRuleSetApplicator.apply();
    const departmentRelatedHisInformationMerger = new DepartmentRelatedHisInformationMerger();
    departmentRelatedHisInformationMerger.mergeDepartmentRelatedHisInformation(this._source.source.hisInformation, this._source.source.attributes.department);
    this._logger.info("診療科HIS項目マージ完了");
    const hisApplicator = new HisInformationApplicator(this._source);
    const progresses = Enumerable.from([ EditPhaseKinds.EditStart ]).concat(phases).distinct().toArray();
    this._source.source.attributes.phases = progresses;
    hisApplicator.applyByProgresses(
      this._source.source.hisInformation,
      progresses,
      parameter.user,
      parameter.department,
      this.getOrderUser(),
      this.getAttendingUser(),
    );
    this._logger.info("HIS情報初期化完了");
    const mischievousApplicator = new MischievousHisInformationApplicator(this._source, this.getUser(), this.isOrderableAuthority());
    mischievousApplicator.apply();
    this._logger.info("特殊HIS情報初期化完了");
    if (this.configuration.behavior.document.editor.patientProfile.isEnable) {
      const patientProfileApplicator = new PatientProfileApplicator(this._source);
      patientProfileApplicator.apply(this._source.source.patientProfile);
      this._logger.info("患者プロフィール適用完了");
    }
    const fontSizeInitializer = new FontSizeInitializer(this._source);
    fontSizeInitializer.initialize();
    this._logger.info("フォントサイズ初期化完了");
    await this._source.externalScriptRunner.executeScriptInDocumentAsync(EditPhaseKinds.EditStart, parameter);
    await this._source.externalScriptRunner.executeStackedScriptsAsync(parameter);
    this._logger.info("外部スクリプト実行完了");
    const itemHeightInitializer = new ItemHeightInitializer(this._source);
    itemHeightInitializer.initialize();
    this._logger.info("オブジェクト高さ初期化完了");
  }

  private getManagerIds(specifiedManagerIds: string[]): string[] {
    return specifiedManagerIds
      ? specifiedManagerIds       // ワークフロー文書：ワークフロー対応責任者
      : [store.getters.user.id];  // 直接記載：指定されていないのでログイン者
  }

  private getOrderUser(): User {
    return store.getters.userAuthority.isOrderable
      ? store.getters.user
      : this._source.source.attributes.orderUser;
  }

  private getAttendingUser(): User {
    return store.getters.userAuthority.isOrderable
      ? store.getters.user
      : this._source.source.attributes.attendingUser;
  }

  private getUser(): User {
    return store.getters.user;
  }

  private isOrderableAuthority(): boolean {
    const authority = store.getters.userAuthority as UserAuthority;
    return authority && authority.isOrderable;
  }

  public async copyExternalTemplateIds(): Promise<boolean> {
    try {
      const parameter = this.createExternalScriptParameter(this._source.source);
      return await this._source.externalScriptRunner.copyExternalTemplateIds(parameter);
    } catch (e) {
      console.warn(e);
      return false;
    }
  }

  private get configuration(): IConfiguration {
    return store.getters.configuration as IConfiguration;
  }

  private readonly _logger = LoggerFactory.getLogger();
}
